import React, { useEffect } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { ArrowRight, Check, AlertCircle, Sparkles } from 'lucide-react';
import { PROBLEM_PAGES, PROBLEM_SEO } from '../problemPages';
import ClientMarquee from '../components/ClientMarquee';
import CTASection from '../components/CTASection';
import Testimonials from '../components/Testimonials';
import useSEO from '../hooks/useSEO';


const ProblemPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const data = PROBLEM_PAGES[slug];
  const seo = PROBLEM_SEO[slug] || {};
  useSEO({ title: seo.title, description: seo.description, path: `/${slug}` });
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!data) return <Navigate to="/" />;

  const highlightHeadline = () => {
    const parts = data.headline.split(data.yellowWord);
    if (parts.length < 2) return data.headline;
    return (
      <>
        {parts[0]}
        <span className="text-[#F5CD1B]">{data.yellowWord}</span>
        {parts.slice(1).join(data.yellowWord)}
      </>
    );
  };

  return (
    <>
      {/* Hero */}
      <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full bg-[#F5CD1B]/6 blur-[140px]"></div>
          <img src={data.heroImage} alt={data.headline} className="absolute right-0 top-0 w-1/2 h-full object-cover opacity-25" onError={(e)=>{e.target.style.display='none'}} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0d] via-[#0b0b0d]/95 to-[#0b0b0d]/40"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-6">{data.eyebrow}</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[64px] font-black text-white leading-[1.05] max-w-5xl">
            {highlightHeadline()}
          </h1>
          <p className="text-white/80 text-lg lg:text-xl leading-relaxed mt-8 max-w-3xl">
            {data.intro}
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <a href="#proposal" className="btn-yellow inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              Get a bespoke proposal <ArrowRight size={16} />
            </a>
            <a href="#approach" className="btn-ghost inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              See how we fix it
            </a>
          </div>
        </div>
      </section>

      <ClientMarquee />

      {/* Symptoms */}
      <section className="py-24 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center">
                <AlertCircle size={18} className="text-red-400" />
              </div>
              <p className="section-label !text-red-400">The problem</p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-white leading-[1.05]">
              {data.symptoms.title}
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              If more than three of these sound familiar, this page was written for you. The good news? Every one of these symptoms has a fix.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {data.symptoms.items.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#111114] border border-white/10 rounded-xl p-5 hover:border-red-500/25 transition-colors">
                <div className="w-6 h-6 rounded-md bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-red-400 text-sm font-bold leading-none">!</span>
                </div>
                <span className="text-white/85 text-sm leading-relaxed">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="py-24 lg:py-28 bg-[#08080a]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 flex items-center justify-center">
                <Sparkles size={18} className="text-[#F5CD1B]" />
              </div>
              <p className="section-label">The Yellow approach</p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05]">
              {data.approachTitle}
            </h2>
            <p className="text-white/70 leading-relaxed mt-6 text-lg">{data.approachSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.approach.map((step) => (
              <div key={step.step} className="bg-[#111114] border border-white/10 rounded-2xl p-8 hover:border-[#F5CD1B]/30 transition-colors">
                <div className="text-[#F5CD1B] font-display text-5xl font-black">{step.step}</div>
                <h3 className="font-display text-2xl font-bold text-white mt-4">{step.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed mt-3">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables / How we help */}
      {data.deliverables && (
        <section className="py-24">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <p className="section-label mb-4">Getting started</p>
              <h2 className="font-display text-4xl md:text-5xl font-black text-white leading-[1.05]">
                {data.deliverables.title}
              </h2>
              <p className="text-white/70 leading-relaxed mt-6">
                Our expert team handles strategy, creative and delivery. We can do it all for you or provide the strategy and training that you can continue with in-house.
              </p>
              <a href="#proposal" className="mt-8 inline-flex items-center gap-2 text-[#F5CD1B] font-semibold group">
                Get a scoped proposal <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
              {data.deliverables.items.map((d, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#111114] border border-white/10 rounded-xl p-5 hover:border-[#F5CD1B]/30 transition-colors">
                  <div className="w-6 h-6 rounded-md bg-[#F5CD1B] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-[#0b0b0d]" strokeWidth={3} />
                  </div>
                  <span className="text-white/85 text-sm leading-relaxed">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.supportModes && (
        <section className="py-24">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-14">
              <p className="section-label mb-4">How we help</p>
              <h2 className="font-display text-4xl md:text-5xl font-black text-white leading-[1.05]">
                {data.supportModes.title}
              </h2>
              <p className="text-white/70 leading-relaxed mt-6 text-lg">
                {data.supportModes.subtitle}
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              {data.supportModes.modes.map((m) => (
                <div key={m.name} className="bg-[#111114] border border-white/10 rounded-2xl p-8 lg:p-10 hover:border-[#F5CD1B]/30 transition-colors flex flex-col">
                  <p className="section-label mb-3">{m.label}</p>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                    {m.name}
                  </h3>
                  {m.image && (
                    <div className="mt-6 rounded-xl overflow-hidden border border-white/10 aspect-[16/10] bg-[#0b0b0d]">
                      <img
                        src={m.image}
                        alt={m.imageAlt || m.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e)=>{e.target.parentElement.style.display='none'}}
                      />
                    </div>
                  )}
                  <p className="text-white/70 leading-relaxed mt-6">
                    {m.summary}
                  </p>
                  <div className="mt-7 space-y-3">
                    {m.items.map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-md bg-[#F5CD1B] flex items-center justify-center shrink-0 mt-0.5">
                          <Check size={14} className="text-[#0b0b0d]" strokeWidth={3} />
                        </div>
                        <span className="text-white/85 text-sm leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <a href="#proposal" className="mt-8 inline-flex items-center gap-2 text-[#F5CD1B] font-semibold group self-start">
                    Get a proposal for this <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Proof stat */}
      {data.proofStat && (
        <section className="py-20 bg-[#08080a]">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <div className="rounded-3xl bg-gradient-to-br from-[#F5CD1B]/10 via-[#111114] to-[#111114] border border-[#F5CD1B]/20 p-10 lg:p-14 text-center">
              <p className="section-label mb-4">The numbers</p>
              <div className="font-display text-6xl md:text-8xl lg:text-[140px] font-black text-[#F5CD1B] leading-none">
                {data.proofStat}
              </div>
              {data.proofLabel && (
                <p className="text-white/80 text-lg lg:text-xl leading-relaxed mt-6 max-w-2xl mx-auto">
                  {data.proofLabel}
                </p>
              )}
              {data.proofExplanation && (
                <p className="text-white/60 text-base leading-relaxed mt-5 max-w-3xl mx-auto">
                  {data.proofExplanation}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Big CTA */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05] max-w-4xl mx-auto">
            {data.ctaHeadline}
          </h2>
          <p className="text-white/70 text-lg lg:text-xl leading-relaxed mt-6 max-w-2xl mx-auto">
            {data.ctaBody}
          </p>
          <div className="flex flex-wrap gap-3 mt-10 justify-center">
            <a href="#proposal" className="btn-yellow inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              Get a bespoke proposal <ArrowRight size={16} />
            </a>
            <Link to="/portfolio" className="btn-ghost inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              See recent work
            </Link>
          </div>
        </div>
      </section>

      <Testimonials />
      <CTASection />
    </>
  );
};

export default ProblemPage;
